import RenderRouter, { embeddedRouter } from '@/routes';
import { createApp } from '@shopify/app-bridge';
import { NavMenu } from '@shopify/app-bridge-react';
import { getSessionToken } from '@shopify/app-bridge-utils';
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import { Crisp } from 'crisp-sdk-web';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { config, convertQueryStringToObject } from './config';
import { EMBEDDED_APP_STATUS, LocationViewChoices } from './constants/enum';
import { LocalStorageKey } from './constants/storages';
import { getLocalStorageItem, setLocalStorageItem } from './helpers/storage';
import { useGetCrispDataQuery, useGetGeneralSettingQuery, useUpdateGeneralSettingMutation } from './redux/query';
import { handleGetSessionToken, handleLoadingSessionToken, sessionTokenSelector } from './redux/slice/auth.slice';
import { handleResetErrorFields } from './redux/slice/error.slice';
import { onboardingSettingSelector } from './redux/slice/onboarding.slice';
import { handleSetSaveBar } from './redux/slice/savebar.slice';
import { ICrisp } from './types';
import { PATH } from './constants/path';
import { handleLocationTab } from './redux/slice/tab.slice';

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const configApp = {
    apiKey: process.env.REACT_APP_API_KEY || '',
    host: new URLSearchParams(location.search).get('host') || '',
    forceRedirect: process.env.REACT_APP_MODE === 'live' && config.scope !== 'omegatheme',
  };
  const isEmbedded = config.embedded === EMBEDDED_APP_STATUS.EMBEDDED;
  const { token: sessionToken } = useSelector(sessionTokenSelector);
  const loginQueryString = getLocalStorageItem(LocalStorageKey.LOGIN_QUERY_STRING) as string;
  const shopState: string = convertQueryStringToObject(loginQueryString)?.shop || '';
  const { pathname } = useLocation();
  const onboardingSettings = useSelector(onboardingSettingSelector);
  const { data: generalSettingData } = useGetGeneralSettingQuery({ shop: config.shop });
  const [updateGeneralSetting] = useUpdateGeneralSettingMutation();

  const { data: crispData } = useGetCrispDataQuery({ shop: config.shop });

  const intCrisp = useCallback((crispInfo: ICrisp) => {
    const appName = 'DingDoong: Delivery Date';
    const tokenId = btoa(config.shop + appName);

    Crisp.configure('a4bdf13a-e64f-4660-9d3a-b282560b6427', {
      autoload: false,
    });
    Crisp.setTokenId(tokenId);

    if (crispInfo) {
      Crisp.user.setEmail(crispInfo.emailShop);
      Crisp.user.setNickname(config.shop);
      Crisp.session.setData({
        name: crispInfo.nameShop,
        app_name: appName,
        email_store: crispInfo.emailShop,
        store: crispInfo.nameShop,
        country: crispInfo.country,
        timezone: crispInfo.timezone,
        date_installed: crispInfo.firstInstalledDate,
        shopify_plan: crispInfo.shopifyPlan,
        dingdoong_app_plan: crispInfo.plan,
        store_created_at: crispInfo.createStoreDate,
        review_history: crispInfo.reviewHistory,
      });
    }

    Crisp.session.setSegments(['dingdoong', 'dingdoong_v1', '3.0.0'], true);
    Crisp.load();

    return () => {
      Crisp.setTokenId();
      Crisp.session.reset();
    };
  }, []);

  useEffect(() => {
    if (crispData && config.scope !== 'omegatheme') {
      const timer = setTimeout(() => {
        intCrisp(crispData.data);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [crispData, intCrisp]);

  useEffect(() => {
    dispatch(
      handleSetSaveBar({
        isShowSaveBar: false,
        isLoadingSaveBar: false,
        isDisableSaveBar: false,
        isCheckSave: false,
      }),
    );
    dispatch(handleResetErrorFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (isEmbedded && !sessionToken) {
      const getToken = async () => {
        try {
          dispatch(handleLoadingSessionToken(true));
          const app = createApp(configApp);
          const sessionToken = await getSessionToken(app);
          dispatch(
            handleGetSessionToken({
              token: sessionToken,
              expiredTime: Date.now() + 60 * 1000,
              isLoading: false,
            }),
          );
        } catch (error) {
          console.log('Error when get session token', error);
        }
      };
      getToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (config && config.shop && config.shop !== shopState) {
      setLocalStorageItem(LocalStorageKey.LOGIN_QUERY_STRING, config.loginQueryString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigate = useCallback((e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    if (generalSettingData?.data.displayOnboarding === true) {
      updateGeneralSetting({
        shop: config.shop,
        displayOnboarding: false,
        onboardingStep: onboardingSettings.step,
      });
    }
    if (path === PATH.LOCATIONS.pathname) {
      dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.ALL }));
    }
    navigate(path);
  }, [dispatch, generalSettingData?.data.displayOnboarding, navigate, onboardingSettings.step, updateGeneralSetting])

  return (
    <AppProvider i18n={enTranslations}>
      {isEmbedded && (
        <NavMenu>
          {embeddedRouter.map(({ label, path }) => {
            return (
              <a
                id={path}
                key={path}
                href={path}
                onClick={(e) => handleNavigate(e, path)}
              >
                {label}
              </a>
            );
          })}
        </NavMenu>
      )}
      <RenderRouter />
    </AppProvider>
  );
}

export default App;
